import React, { useEffect, useState } from "react";
import { Radio, Tag, Badge, Spin, Button, Modal, notification } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import styles from "./style.module.scss";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "redux/Dashboard/actions.js";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import uniqueTermsByCount from "./uniqueTermsByCount";

const HotTerms = ({
  allNgrams,
  loading,
  setLoading,
  overLimit,
  disableSelection,
  onTermBlacklisted, // Add callback prop to notify parent
}) => {
  const [ngrams, setNgrams] = useState(null);
  const [terms, setTerms] = useState([]);
  const [showBlacklistModal, setShowBlacklistModal] = useState(false);
  const [termToBlacklist, setTermToBlacklist] = useState(null);
  const [blacklisting, setBlacklisting] = useState(false);
  const DashboardReducer = useSelector((state) => state.DashboardReducer);
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const dispatch = useDispatch();
  const [noNgrams, setNoNgrams] = useState(false);
  const [defaultValue, setDefaultValue] = useState(null);
  const { t } = useTranslation();
  const history = useHistory();
  const params = new URLSearchParams(window.location.search);

  useEffect(() => {
    if (allNgrams && Object.keys(allNgrams).length > 0) {
      let found = false;
      for (let i = Object.keys(allNgrams).length; i > 0; i--) {
        if (allNgrams[i] && allNgrams[i].length > 0) {
          setNgrams(i);
          found = true;
          break;
        }
      }
      if (!found) {
        setNoNgrams(true);
      }
    }
  }, [allNgrams]);

  const addHotTerm = (termsObj) => {
    const term = termsObj.terms;
    const similarTerms = [];
    if (
      termsObj?.hasOwnProperty("similar_ngrams") &&
      termsObj?.similar_ngrams?.length > 0
    ) {
      termsObj.similar_ngrams.forEach((ngram) =>
        similarTerms.push(ngram.terms)
      );
    }
    const selectedTerms = params.has("hotterm")
      ? JSON.parse(params.get("hotterm"))
      : [];
    if (selectedTerms.term === term) {
      params.delete("hotterm");
      dispatch({
        type: actions.UPDATE_SEARCH_STATE,
        payload: {
          chosenHotTerm: [],
        },
      });
    } else {
      params.delete("hotterm");
      params.append(
        "hotterm",
        JSON.stringify({
          term,
          similarTerms,
        })
      );
      dispatch({
        type: actions.UPDATE_SEARCH_STATE,
        payload: {
          chosenHotTerm: [term, ...similarTerms],
        },
      });
    }
    history.push({ search: params.toString() });
    dispatch({
      type: actions.UPDATE_Dashboard,
      payload: {
        showNewReviewsLabel: true,
      },
    });
  };

  const handleBlacklistTerm = (term) => {
    setTermToBlacklist(term);
    setShowBlacklistModal(true);
  };

  const confirmBlacklist = async () => {
    if (!termToBlacklist) return;
    
    setBlacklisting(true);
    try {
      const result = await apiRequest("users/hotterms/blacklist/add", "POST", {
        term: termToBlacklist.terms,
        term_type: ngrams, // 1, 2, 3, or 4 for n-gram type
        organization_id: DashboardReducer.dashboard?.OrganizationId,
      }, true);

      if (result.success) {
        notification.success({
          message: t("dashboard_tooltips.hotterms.blacklist.success", "Term successfully blacklisted"),
        });
        setShowBlacklistModal(false);
        setTermToBlacklist(null);
        
        // Remove the blacklisted term from the current state
        if (terms) {
          const updatedTerms = terms.filter(term => term.terms !== termToBlacklist.terms);
          setTerms(updatedTerms);
        }
        
        // Notify parent component about the blacklisted term
        if (onTermBlacklisted) {
          onTermBlacklisted(termToBlacklist.terms, ngrams);
        }
      } else {
        notification.error({
          message: result.error || t("dashboard_tooltips.hotterms.blacklist.error", "Failed to blacklist term"),
        });
      }
    } catch (error) {
      notification.error({
        message: t("dashboard_tooltips.hotterms.blacklist.error", "Failed to blacklist term"),
      });
    } finally {
      setBlacklisting(false);
    }
  };

  const visualTerms = terms?.map((term) => {
    return (
      <div key={term.terms} className={styles.termContainer}>
        <Tag
          icon={
            <Badge
              count={term.count}
              overflowCount={9999}
              style={{
                marginRight: "10px",
                background: "#FFFFFF",
                border: "none",
                color: "#4400AA",
              }}
            />
          }
          className={
            term.terms === DashboardReducer.searchState.chosenHotTerm
              ? styles.activeTerm
              : ""
          }
          color={theme === "brand" ? "#4a4d4e" : "#4400AA"}
          onClick={() => !disableSelection && addHotTerm(term)}
          title={term.terms}
          style={{ position: 'relative', paddingRight: '30px' }}
        >
          {term.terms}
          <Button
            type="text"
            icon={<CloseOutlined style={{ fontSize: '10px' }} />}
            size="small"
            className={styles.removeButton}
            onClick={(e) => {
              e.stopPropagation();
              handleBlacklistTerm(term);
            }}
            title={t("dashboard_tooltips.hotterms.remove", "Remove term")}
            style={{
              position: 'absolute',
              right: '5px',
              top: '50%',
              transform: 'translateY(-50%)',
              padding: '0',
              minWidth: 'auto',
              height: 'auto',
              transition: 'opacity 0.2s ease'
            }}
          />
        </Tag>
      </div>
    );
  });

  useEffect(() => {
    if (
      allNgrams &&
      Object.values(allNgrams).flat()?.length > 0 &&
      !overLimit
    ) {
      setNoNgrams(false);
      setTerms(uniqueTermsByCount(allNgrams[ngrams]));
    }
  }, [allNgrams, ngrams]);
  useEffect(() => {
    if (
      (allNgrams && Object.values(allNgrams).flat()?.length === 0) ||
      overLimit
    ) {
      setNoNgrams(true);
      setTerms([]);
    }
  }, [allNgrams, overLimit]);
  useEffect(() => {
    if (ngrams) {
      setDefaultValue(ngrams);
    }
  }, [allNgrams, ngrams]);

  return (
    <div className={styles.HotWordsSection}>
      <Spin spinning={loading}>
        <div className={styles.radioContainer}>
          {allNgrams && ngrams && defaultValue && (
            <Radio.Group
              defaultValue={defaultValue}
              value={defaultValue}
              buttonStyle="solid"
              onChange={(e) => setNgrams(e.target.value)}
            >
              {[1, 2, 3, 4].map((i) => (
                <>
                  {allNgrams[i] && allNgrams[i].length > 0 && (
                    <Radio.Button
                      style={{
                        display:
                          allNgrams[i] && allNgrams[i].length === 0
                            ? "none"
                            : "",
                      }}
                      checked={i === defaultValue}
                      value={i}
                    >
                      {t("selected_dashboard_page.101", {
                        n: i,
                      })}
                    </Radio.Button>
                  )}
                </>
              ))}
            </Radio.Group>
          )}
        </div>

        <div className={styles.description}>
          {noNgrams ? (
            <></>
          ) : (
            <span>
              {t("selected_dashboard_page.102", {
                n: ngrams,
              })}
            </span>
          )}
        </div>
        <div className={styles.visualTerms}>
          {noNgrams ? (
            overLimit ? (
              <p className={styles.noNgramsMsg}>
                {t("selected_dashboard_page.103")}
              </p>
            ) : (
              <p className={styles.noNgramsMsg}>
                {t("selected_dashboard_page.104")}
              </p>
            )
          ) : (
            visualTerms
          )}
        </div>
      </Spin>

      <Modal
        title={t("dashboard_tooltips.hotterms.blacklist.confirm_title", "Blacklist Hot Term")}
        open={showBlacklistModal}
        onOk={confirmBlacklist}
        onCancel={() => {
          setShowBlacklistModal(false);
          setTermToBlacklist(null);
        }}
        confirmLoading={blacklisting}
        okText={t("dashboard_tooltips.hotterms.blacklist.confirm", "Blacklist")}
        cancelText={t("common.cancel", "Cancel")}
      >
        <p>
          {t("dashboard_tooltips.hotterms.blacklist.confirm_message", "Are you sure you want to blacklist the term")}
          <span className={styles.hottermText}>"{termToBlacklist?.terms}"</span>?
        </p>
        <p>
          {t("dashboard_tooltips.hotterms.blacklist.confirm_description", "This term will be hidden from all hot terms results for your organization. You can view and manage all blacklisted terms in the ")}
          <a 
            href="/settings/preferences" 
            className={styles.prefsLink}
            onClick={(e) => {
              e.preventDefault();
              setShowBlacklistModal(false);
              setTermToBlacklist(null);
              history.push("/settings/preferences");
            }}
          >
            {t("dashboard_tooltips.hotterms.blacklist.preferences_link", "Preferences page")}
          </a>
          .
        </p>
      </Modal>
    </div>
  );
};

export default HotTerms;
